import { useCallback, useMemo, useState } from 'react';

import type { BarcodeFieldError } from './components/barcode-field/barcode-field';
import { validateBarcodeManual } from './components/barcode-field/barcode-field';
import { CONTAINS_ID_UNSET, DIMENSION_MAX, QUANTITY_MAX } from './uom-modal.constants';
import type { UomFormState } from '../../uom-types';

export type UomValidationResult = {
  errors: Record<string, string | null>;
  barcodeError: BarcodeFieldError;
  isDuplicateBarcode: boolean;
  isDuplicateUomSku: boolean;
  isValid: boolean;
};

export const computeValidation = (
  form: UomFormState,
  effectiveBarcode: string,
  existingUnits: { barcode?: string | null; uomSku?: string | null; productUnitOfMeasureId: number }[],
  currentRowId: number | undefined,
): UomValidationResult => {
  const errors: Record<string, string | null> = {};
  if (!form.uomTypeId) errors.uomTypeId = 'required';
  if (form.containsId === CONTAINS_ID_UNSET) errors.containsId = 'required';
  const qty = Number(form.quantity);
  if (!form.quantity || qty < 1 || !Number.isInteger(qty)) errors.quantity = 'required';
  else if (qty > QUANTITY_MAX) errors.quantity = 'tooLarge';
  for (const field of ['length', 'width', 'height'] as const) {
    const raw = form[field];
    if (raw !== '') {
      const n = Number(raw);
      if (Number.isNaN(n) || n < 0) errors[field] = 'invalid';
      else if (n > DIMENSION_MAX) errors[field] = 'tooLarge';
    }
  }
  let barcodeError: BarcodeFieldError = null;
  if (!form.barcodeIsAuto && form.barcode) {
    barcodeError = validateBarcodeManual(form.barcode);
  }
  const isDuplicateBarcode =
    !!effectiveBarcode && existingUnits.some((u) => u.barcode === effectiveBarcode && u.productUnitOfMeasureId !== currentRowId);
  const isDuplicateUomSku =
    !!form.uomSku && existingUnits.some((u) => u.uomSku === form.uomSku && u.productUnitOfMeasureId !== currentRowId);
  const isValid = Object.values(errors).every((v) => !v) && !barcodeError && !isDuplicateBarcode && !isDuplicateUomSku;
  return { errors, barcodeError, isDuplicateBarcode, isDuplicateUomSku, isValid };
};

type UseUomValidationParams = {
  form: UomFormState;
  effectiveBarcode: string;
  existingUnits: { barcode?: string | null; uomSku?: string | null; productUnitOfMeasureId: number }[];
  currentRowId: number | undefined;
};

export const useUomValidation = ({ form, effectiveBarcode, existingUnits, currentRowId }: UseUomValidationParams) => {
  const [errors, setErrors] = useState<Record<string, string | null>>({});
  const [barcodeError, setBarcodeError] = useState<BarcodeFieldError>(null);

  const validation = useMemo(
    () => computeValidation(form, effectiveBarcode, existingUnits, currentRowId),
    [form, effectiveBarcode, existingUnits, currentRowId],
  );

  const applyValidation = useCallback((): boolean => {
    setErrors(validation.errors);
    setBarcodeError(validation.barcodeError);
    return validation.isValid;
  }, [validation]);

  return { errors, setErrors, barcodeError, setBarcodeError, validation, applyValidation };
};
